import { nanoid } from "nanoid";
import {
	AGENT_CHAT_MAX_ATTACHMENTS,
	AGENT_CHAT_MAX_ATTACHMENT_BYTES,
	AGENT_CHAT_TEXT_ATTACHMENT_EXTENSIONS,
} from "./constants";
import type { AgentChatAttachment } from "./types";

export type AgentChatAttachmentError =
	| "too-many"
	| "too-large"
	| "unsupported"
	| "read-failed";

export type AgentChatAttachmentProcessResult = {
	attachments: AgentChatAttachment[];
	errors: Array<{
		name: string;
		reason: AgentChatAttachmentError;
	}>;
};

function getExtension(filename: string) {
	const lastDot = filename.lastIndexOf(".");
	if (lastDot < 0) {
		return "";
	}
	return filename.slice(lastDot).toLowerCase();
}

function isImageFile(file: File) {
	return file.type.startsWith("image/");
}

function isTextExtension(extension: string) {
	return (AGENT_CHAT_TEXT_ATTACHMENT_EXTENSIONS as readonly string[]).includes(
		extension,
	);
}

function isTextFile(file: File) {
	if (isTextExtension(getExtension(file.name))) {
		return true;
	}

	return (
		file.type === "text/plain" ||
		file.type === "text/markdown" ||
		file.type === "text/csv" ||
		file.type === "application/json"
	);
}

function readFileAsBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result;
			if (typeof result !== "string") {
				reject(new Error("Failed to read file"));
				return;
			}

			const commaIndex = result.indexOf(",");
			resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
		};
		reader.onerror = () => {
			reject(reader.error ?? new Error("Failed to read file"));
		};
		reader.readAsDataURL(file);
	});
}

function readFileAsText(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result;
			if (typeof result !== "string") {
				reject(new Error("Failed to read file"));
				return;
			}
			resolve(result);
		};
		reader.onerror = () => {
			reject(reader.error ?? new Error("Failed to read file"));
		};
		reader.readAsText(file);
	});
}

export function formatAgentChatAttachmentError(
	name: string,
	reason: AgentChatAttachmentError,
) {
	switch (reason) {
		case "too-many":
			return `Too many files (max ${AGENT_CHAT_MAX_ATTACHMENTS})`;
		case "too-large":
			return `${name} is too large (max ${Math.round(AGENT_CHAT_MAX_ATTACHMENT_BYTES / (1024 * 1024))} MB)`;
		case "unsupported":
			return `${name} is not supported (images or .txt/.md/.csv/.json)`;
		case "read-failed":
			return `Could not read ${name}`;
	}
}

export async function processAgentChatFiles(
	files: FileList | File[],
	existingCount: number,
): Promise<AgentChatAttachmentProcessResult> {
	const fileList = Array.from(files);
	const attachments: AgentChatAttachment[] = [];
	const errors: AgentChatAttachmentProcessResult["errors"] = [];
	let remainingSlots = AGENT_CHAT_MAX_ATTACHMENTS - existingCount;

	if (remainingSlots <= 0) {
		for (const file of fileList) {
			errors.push({ name: file.name, reason: "too-many" });
		}
		return { attachments, errors };
	}

	for (const file of fileList) {
		if (remainingSlots <= 0) {
			errors.push({ name: file.name, reason: "too-many" });
			continue;
		}

		if (file.size > AGENT_CHAT_MAX_ATTACHMENT_BYTES) {
			errors.push({ name: file.name, reason: "too-large" });
			continue;
		}

		if (isImageFile(file)) {
			try {
				const data = await readFileAsBase64(file);
				attachments.push({
					id: nanoid(),
					name: file.name,
					mediaType: file.type || "image/png",
					kind: "image",
					data,
				});
				remainingSlots -= 1;
			} catch {
				errors.push({ name: file.name, reason: "read-failed" });
			}
			continue;
		}

		if (isTextFile(file)) {
			try {
				const data = await readFileAsText(file);
				attachments.push({
					id: nanoid(),
					name: file.name,
					mediaType: file.type || "text/plain",
					kind: "text",
					data,
				});
				remainingSlots -= 1;
			} catch {
				errors.push({ name: file.name, reason: "read-failed" });
			}
			continue;
		}

		errors.push({ name: file.name, reason: "unsupported" });
	}

	return { attachments, errors };
}

export function buildUserMessageTextWithAttachments(
	content: string,
	attachments: AgentChatAttachment[] | undefined,
) {
	const trimmed = content.trim();
	const textAttachments = (attachments ?? []).filter(
		(attachment) => attachment.kind === "text",
	);

	if (textAttachments.length === 0) {
		return trimmed;
	}

	const fileBlocks = textAttachments
		.map(
			(attachment) =>
				`--- Attached file: ${attachment.name} ---\n${attachment.data}`,
		)
		.join("\n\n");

	if (!trimmed) {
		return fileBlocks;
	}

	return `${trimmed}\n\n${fileBlocks}`;
}
